import { css } from 'styled-components';

const typography = {
  fonts: {
    primary: "'Inter', sans-serif", // Modern, highly readable font
    secondary: "'Urbanist', sans-serif", // Contemporary, geometric font
    mono: "'Fira Code', monospace", // Premium coding font
    accent: "'Cabinet Grotesk', sans-serif" // For special elements
  },
  weights: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  sizes: {
    xs: '0.75rem',      // 12px
    sm: '0.875rem',     // 14px
    base: '1rem',       // 16px
    lg: '1.125rem',     // 18px
    xl: '1.25rem',      // 20px
    '2xl': '1.5rem',    // 24px
    '3xl': '2rem',      // 32px
    '4xl': '2.5rem',    // 40px
    '5xl': '3rem',      // 48px
    '6xl': '3.75rem',   // 60px
    '7xl': '4.5rem',    // 72px
    '8xl': '6rem',      // 96px
  },
  lineHeights: {
    none: 1,
    tight: 1.2,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  }
};

// Enhanced Typography Mixins
const headingStyles = css`
  font-family: ${typography.fonts.secondary};
  font-weight: ${typography.weights.bold};
  letter-spacing: -0.03em;
  line-height: ${typography.lineHeights.tight};
`;

const bodyStyles = css`
  font-family: ${typography.fonts.primary};
  font-weight: ${typography.weights.regular};
  letter-spacing: -0.01em;
  line-height: ${typography.lineHeights.relaxed};
`;

const accentStyles = css`
  font-family: ${typography.fonts.accent};
  font-weight: ${typography.weights.medium};
  letter-spacing: 0.02em;
`;

export const lightTheme = {
  // Primary Colors - Professional deep blue palette
  primary: '#1e3a8a',      // Rich navy blue - main brand color
  primaryLight: '#3b82f6',
  primaryDark: '#1e40af',
  primaryMuted: '#bfdbfe',
  
  // Secondary Colors - Modern slate accents
  secondary: '#334155',    // Sophisticated slate
  secondaryLight: '#64748b',
  secondaryDark: '#0f172a',
  secondaryMuted: '#e2e8f0',
  
  // Accent Colors - Vibrant touches
  accent: '#6366f1',      // Indigo accent
  accentLight: '#818cf8',
  accentDark: '#4f46e5',
  
  // Background Colors - Clean and crisp
  background: '#ffffff',
  backgroundAlt: '#f8fafc',
  backgroundDark: '#f1f5f9',
  backgroundGradient: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
  
  // Text Colors - High contrast for readability
  text: '#0f172a',
  textLight: '#475569',
  textMuted: '#94a3b8',
  textInverse: '#ffffff',
  
  // UI Elements
  navbar: 'rgba(255, 255, 255, 0.95)',
  card: '#ffffff',
  border: '#e2e8f0',
  divider: '#e2e8f0',
  
  // Status Colors - Clear feedback
  success: '#059669',
  error: '#dc2626',
  warning: '#d97706',
  info: '#0284c7',
  
  // Gradients - Sophisticated overlays
  gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
  gradientAlt: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
  gradientAccent: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
  
  // Shadows - Refined elevation
  shadowXs: '0 1px 2px rgba(15, 23, 42, 0.05)',
  shadowSm: '0 2px 4px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
  shadowMd: '0 4px 6px rgba(15, 23, 42, 0.08), 0 2px 4px rgba(15, 23, 42, 0.04)',
  shadowLg: '0 10px 15px rgba(15, 23, 42, 0.07), 0 4px 6px rgba(15, 23, 42, 0.05)',
  shadowXl: '0 20px 25px rgba(15, 23, 42, 0.08), 0 10px 10px rgba(15, 23, 42, 0.04)',
  
  // Animations
  transition: {
    fast: '0.15s ease-in-out',
    normal: '0.25s ease-in-out',
    slow: '0.4s ease-in-out',
  },
  
  ...typography,
  mixins: {
    headingStyles,
    bodyStyles,
    accentStyles,
  },
};

export const darkTheme = {
  // Primary Colors - Illuminated dark mode
  primary: '#3b82f6',     // Brighter blue for dark mode
  primaryLight: '#60a5fa',
  primaryDark: '#2563eb',
  primaryMuted: '#1d4ed8',
  
  // Secondary Colors
  secondary: '#94a3b8',
  secondaryLight: '#cbd5e1',
  secondaryDark: '#64748b',
  secondaryMuted: '#334155',
  
  // Accent Colors
  accent: '#818cf8',
  accentLight: '#a5b4fc',
  accentDark: '#6366f1',
  
  // Background Colors - Deep and rich
  background: '#0f172a',
  backgroundAlt: '#1e293b',
  backgroundDark: '#0f172a',
  backgroundGradient: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
  
  // Text Colors - Optimal contrast
  text: '#f8fafc',
  textLight: '#e2e8f0',
  textMuted: '#94a3b8',
  textInverse: '#0f172a',
  
  // UI Elements
  navbar: 'rgba(15, 23, 42, 0.95)',
  card: '#1e293b',
  border: '#334155',
  divider: '#334155',
  
  // Status Colors - Adjusted for dark mode
  success: '#10b981',
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#0ea5e9',
  
  // Gradients - Ethereal dark mode
  gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
  gradientAlt: 'linear-gradient(135deg, #818cf8 0%, #3b82f6 100%)',
  gradientAccent: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
  
  // Shadows - Dramatic depth
  shadowXs: '0 1px 2px rgba(0, 0, 0, 0.3)',
  shadowSm: '0 2px 4px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3)',
  shadowMd: '0 4px 6px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.4)',
  shadowLg: '0 10px 15px rgba(0, 0, 0, 0.6), 0 4px 6px rgba(0, 0, 0, 0.5)',
  shadowXl: '0 20px 25px rgba(0, 0, 0, 0.7), 0 10px 10px rgba(0, 0, 0, 0.6)',
  
  // Animations
  transition: {
    fast: '0.15s ease-in-out',
    normal: '0.25s ease-in-out',
    slow: '0.4s ease-in-out',
  },
  
  ...typography,
  mixins: {
    headingStyles,
    bodyStyles,
    accentStyles,
  },
};